/**
 * Shared API service for Bills API calls and postMessage communication
 */

import devConfig from '../config/dev.json';
import prodConfig from '../config/prod.json';

const config = process.env.REACT_APP_ENV === 'prod' ? prodConfig : devConfig;
const API_BASE_URL = config.apiBaseUrl;

// The API answers 403 with one of these codes when the selected org requires MFA or SSO and this login did not use it.
const LOGIN_POLICY_CODES = ['login_mfa_required', 'login_sso_required'];
let onLoginPolicy = null;

/** Registers what to do when the API asks for a stronger login (App sends the user back through Auth0). */
export const setLoginPolicyHandler = (handler) => { onLoginPolicy = handler; };

/** Every call to the API goes through here, so a login-policy answer is noticed wherever it comes from. */
const apiFetch = async (...args) => {
  const response = await fetch(...args);
  if (response.status === 403 && onLoginPolicy) {
    const body = await response.clone().json().catch(() => null);
    if (body && LOGIN_POLICY_CODES.includes(body.code)) onLoginPolicy(body.code);
  }
  return response;
};

/** Adds the selected organisation to an org-scoped endpoint; without one the API uses its legacy default. */
export const withOrg = (url, orgId) =>
  orgId ? `${url}${url.includes('?') ? '&' : '?'}orgId=${encodeURIComponent(orgId)}` : url;

/**
 * Call the getUserString API and send result to parent window
 * @param {string} accessToken - Auth0 access token
 * @param {boolean} isAuthenticated - Whether user is authenticated
 */
export const callUserStringApi = async (accessToken, isAuthenticated) => {
  console.log('Calling API...');
  
  // If user is not authenticated, send status message immediately
  if (!isAuthenticated) {
    console.log('User not authenticated, sending logout status');
    sendPostMessage({
      userString: '',
      apiResponse: null,
      error: 'User not authenticated',
      status: 'logged_out'
    });
    return;
  }
  
  try {
    // Call API to get userString with authentication
    const response = await apiFetch(`${API_BASE_URL}/getUserString`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'ngrok-skip-browser-warning': 'true'
      }
    });

    console.log('API response status:', response.status);
    
    if (response.ok) {
      const responseText = await response.text();
      console.log('API response text:', responseText);
      
      // Send to parent WordPress site via postMessage
      sendPostMessage({
        userString: responseText,
        apiResponse: responseText,
        status: 'authenticated'
      });
    } else {
      console.error('API call failed:', response.status, response.statusText);
      // Send postMessage with empty string when API fails
      sendPostMessage({
        userString: '',
        apiResponse: null,
        error: `API failed: ${response.status}`,
        status: 'api_error'
      });
    }
  } catch (error) {
    console.error('Error calling API:', error);
    // Send postMessage with empty string when authentication or other errors occur
    sendPostMessage({
      userString: '',
      apiResponse: null,
      error: error.message,
      status: 'error'
    });
  }
};

/**
 * Send postMessage to parent window
 * @param {Object} data - Data to send
 */
export const sendPostMessage = (data) => {
  if (window.parent) {
    const message = {
      type: 'USER_STRING_UPDATE',
      ...data
    };
    
    console.log('Sending postMessage to parent window:', message);
    window.parent.postMessage(message, '*');
  } else {
    console.log('No parent window found for postMessage');
  }
};

/**
 * Set up polling for the API
 * @param {Function} apiCallFunction - Function to call the API
 * @param {number} intervalMs - Polling interval in milliseconds
 * @returns {Object} Object with start and stop methods
 */
export const createApiPoller = (apiCallFunction, intervalMs = 60000) => {
  let intervalRef = null;
  
  const start = () => {
    if (intervalRef) {
      console.log('Polling already started');
      return;
    }
    
    console.log(`Starting API polling every ${intervalMs / 1000} seconds`);
    intervalRef = setInterval(() => {
      console.log('Polling API...');
      apiCallFunction();
    }, intervalMs);
  };
  
  const stop = () => {
    if (intervalRef) {
      clearInterval(intervalRef);
      intervalRef = null;
      console.log('Polling stopped');
    }
  };
  
  return { start, stop };
};

/**
 * Fetch all organisations for the current user from Bills API
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<any>} Parsed JSON response
 */
export const fetchOrganisationsForUser = async (accessToken) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await apiFetch(`${API_BASE_URL}/organisations`, {
    method: 'GET',
    headers
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`organisations failed: ${response.status} ${text}`);
  }

  return response.json();
};

/**
 * Fetch a single organisation by ID from Bills API
 * @param {number} orgId - Organisation ID
 * @param {string} accessToken - optional Auth0 access token
 * @returns {Promise<any>} Parsed JSON response
 */
export const fetchOrganisation = async (orgId, accessToken) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await apiFetch(`${API_BASE_URL}/organisation?orgId=${orgId}`, {
    method: 'GET',
    headers
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`organisation failed: ${response.status} ${text}`);
  }

  return response.json();
};

/**
 * Fetch all organisations from Bills API
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<any>} Parsed JSON response
 */
export const fetchAllOrganisations = async (accessToken) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await apiFetch(`${API_BASE_URL}/organisationReadAll`, {
    method: 'GET',
    headers
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`organisationReadAll failed: ${response.status} ${text}`);
  }

  return response.json();
};

/**
 * Create a new organisation via Bills API
 * @param {Object} payload - Body matching DC_Create_Org schema
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<any>} Parsed JSON response
 */
export const deleteOrganisation = async (orgId, accessToken) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  const response = await apiFetch(`${API_BASE_URL}/organisation?orgId=${orgId}`, {
    method: 'DELETE',
    headers
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`organisation delete failed: ${response.status} ${text}`);
  }

  return response.json();
};

export const createOrganisation = async (payload, accessToken) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await apiFetch(`${API_BASE_URL}/organisation/create`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload || {})
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Create_Organisation failed: ${response.status} ${text}`);
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json') || contentType.includes('text/json')) {
    return response.json();
  }
  return response.text();
};

/**
 * Fetch all members for an organisation
 * @param {number} orgId - Organisation ID
 * @param {string} accessToken - optional Auth0 access token
 * @returns {Promise<any>} Parsed JSON response
 */
export const fetchOrganisationMembers = async (orgId, accessToken) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await apiFetch(`${API_BASE_URL}/organisation/members?orgId=${orgId}`, {
    method: 'GET',
    headers
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`organisation/members failed: ${response.status} ${text}`);
  }

  return response.json();
};

/** The org's roles (Admin and Member). */
export const fetchOrgRoles = async (orgId, accessToken) => {
  const headers = { 'Content-Type': 'application/json', 'ngrok-skip-browser-warning': 'true' };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  const response = await apiFetch(`${API_BASE_URL}/organisation/roles?orgId=${orgId}`, { method: 'GET', headers });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`organisation/roles failed: ${response.status} ${text}`);
  }
  return response.json();
};

/** Gives a member one role ({ orgId, userId, roleId }); the API refuses unless the caller is the owner or an Admin. */
export const setOrgMemberRole = async (payload, accessToken) => {
  const headers = { 'Content-Type': 'application/json', 'ngrok-skip-browser-warning': 'true' };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  const response = await apiFetch(`${API_BASE_URL}/organisation/member/role`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(payload)
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.Error || `Changing the role failed (${response.status})`);
  }
  return response.json();
};

export const updateOrganisationMember = async (payload, accessToken) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  const response = await apiFetch(`${API_BASE_URL}/organisation/member`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`organisation/member update failed: ${response.status} ${text}`);
  }

  return response.json();
};

/**
 * Rotate a member's org security key
 * @param {Object} payload - { orgId, userId }
 * @param {string} accessToken - optional Auth0 access token
 * @returns {Promise<any>} Parsed JSON response
 */
export const rotateOrgMemberKey = async (payload, accessToken) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  const response = await apiFetch(`${API_BASE_URL}/organisation/member/rotate-key`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`rotate-key failed: ${response.status} ${text}`);
  }

  return response.json();
};

/**
 * Add a member to an organisation
 * @param {Object} payload - { orgId, emailAddress, firstName, lastName }
 * @param {string} accessToken - optional Auth0 access token
 * @returns {Promise<any>} Parsed JSON response
 */
export const addOrganisationMember = async (payload, accessToken) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await apiFetch(`${API_BASE_URL}/organisation/member`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`organisation/member failed: ${response.status} ${text}`);
  }

  return response.json();
};

/**
 * Fetch all photos from Bills API
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<any>} Parsed JSON response with photo URLs
 */
export const fetchAllPhotos = async (accessToken) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await apiFetch(`${API_BASE_URL}/photoGetAll`, {
    method: 'GET',
    headers
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`photoGetAll failed: ${response.status} ${text}`);
  }

  return response.json();
};

/**
 * Fetch all suppliers from Bills API
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<any>} Parsed JSON response with suppliers data
 */
export const fetchSuppliers = async (accessToken, orgId) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await apiFetch(withOrg(`${API_BASE_URL}/suppliers`, orgId), {
    method: 'GET',
    headers
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`suppliers failed: ${response.status} ${text}`);
  }

  return response.json();
};

/**
 * Connect a supplier via Bills API
 * @param {Object} connectionData - Connection data with emailAddress, password, serviceId, Id
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<any>} Parsed JSON response
 */
export const connectSupplier = async (connectionData, accessToken, orgId) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await apiFetch(withOrg(`${API_BASE_URL}/supplier/connect`, orgId), {
    method: 'POST',
    headers,
    body: JSON.stringify(connectionData)
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`supplier/connect failed: ${response.status} ${text}`);
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json') || contentType.includes('text/json')) {
    return response.json();
  }
  return response.text();
};

/**
 * Fetch job run history for a supplier
 * @param {number} supplierId - Supplier ID
 * @param {string} accessToken - optional Auth0 access token
 * @returns {Promise<any>} Parsed JSON response
 */
export const fetchSupplierJobs = async (supplierId, accessToken, orgId) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await apiFetch(withOrg(`${API_BASE_URL}/supplier/jobs?id=${supplierId}`, orgId), {
    method: 'GET',
    headers
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`supplier/jobs failed: ${response.status} ${text}`);
  }

  return response.json();
};

/**
 * Sync a supplier via Bills API
 * @param {Object} syncData - Sync data with serviceId and Id
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<any>} Parsed JSON response
 */
export const syncSupplier = async (syncData, accessToken, orgId) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await apiFetch(withOrg(`${API_BASE_URL}/supplier/sync`, orgId), {
    method: 'POST',
    headers,
    body: JSON.stringify(syncData)
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`supplier/sync failed: ${response.status} ${text}`);
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json') || contentType.includes('text/json')) {
    return response.json();
  }
  return response.text();
};

/**
 * Delete a photo via Bills API
 * @param {string} photoId - ID of the photo to delete
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<any>} Parsed JSON response
 */
export const deleteReceipt = async (fileId, accessToken, orgId) => {
  const headers = {};
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await apiFetch(withOrg(`${API_BASE_URL}/receipt/Delete?fileId=${encodeURIComponent(fileId)}`, orgId), {
    method: 'DELETE',
    headers
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`receipt/Delete failed: ${response.status} ${text}`);
  }

  return response.json();
};

/**
 * Upload photo to Bills API
 * @param {File} file - File to upload
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @param {Array<string>} params - optional parameters array
 * @returns {Promise<any>} Parsed response
 */
export const photoUpload = async (file, accessToken, params = []) => {
  const headers = {
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const formData = new FormData();
  formData.append('Files', file);
  
  // Add params if provided
  params.forEach(param => {
    formData.append('Params', param);
  });

  const response = await apiFetch(`${API_BASE_URL}/photoUpload`, {
    method: 'POST',
    headers,
    body: formData
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`photoUpload failed: ${response.status} ${text}`);
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json') || contentType.includes('text/json')) {
    return response.json();
  }
  return response.text();
};

/**
 * Fetch all receipts (service fulfillment configs) from Bills API
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<any>} Parsed JSON response with receipts data
 */
export const fetchReceipts = async (accessToken, orgId) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await apiFetch(withOrg(`${API_BASE_URL}/receipts`, orgId), {
    method: 'GET',
    headers
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`receipts failed: ${response.status} ${text}`);
  }

  return response.json();
};

/**
 * Get image content from Bills API
 * @param {number} fileId - ID of the file to retrieve
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<Blob>} Image blob data
 */
export const getImage = async (fileId, accessToken) => {
  const headers = {
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await apiFetch(`${API_BASE_URL}/getImage?id=${fileId}`, {
    method: 'GET',
    headers
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`getImage failed: ${response.status} ${text}`);
  }

  return response.blob();
};

const postSupplierAction = async (action, supplier, accessToken, orgId) => {
  const headers = { 'Content-Type': 'application/json' };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await apiFetch(withOrg(`${API_BASE_URL}/supplier/${action}`, orgId), {
    method: 'POST',
    headers,
    body: JSON.stringify({ Id: supplier.id, serviceId: supplier.serviceId })
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`supplier/${action} failed: ${response.status} ${text}`);
  }

  return response.json();
};

/** Stops scheduled syncs for a connection and cancels any sync still queued. */
export const pauseSupplier = (supplier, accessToken, orgId) => postSupplierAction('pause', supplier, accessToken, orgId);

/** Lets scheduled syncs run again for a connection. */
export const resumeSupplier = (supplier, accessToken, orgId) => postSupplierAction('resume', supplier, accessToken, orgId);

/** Removes a connection and its saved login; receipts already collected stay. */
export const disconnectSupplier = (supplier, accessToken, orgId) => postSupplierAction('disconnect', supplier, accessToken, orgId);

/** The suppliers BillsMax plans to support (the supported ones come from fetchSuppliers). */
export const fetchCatalogue = async (accessToken) => {
  const headers = { 'Content-Type': 'application/json' };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await apiFetch(`${API_BASE_URL}/catalogue`, { method: 'GET', headers });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`catalogue failed: ${response.status} ${text}`);
  }

  return response.json();
};

/** Asks for a supplier BillsMax does not collect from yet. */
export const requestSupplier = async (name, accessToken, orgId) => {
  const headers = { 'Content-Type': 'application/json' };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await apiFetch(withOrg(`${API_BASE_URL}/catalogue/request`, orgId), {
    method: 'POST',
    headers,
    body: JSON.stringify({ name })
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`catalogue/request failed: ${response.status} ${text}`);
  }

  return response.json();
};

/** Sets up a supplier BillsMax does not support yet, for the selected org: { Id, Existing }. */
export const addCustomSupplier = async (name, loginUrl, accessToken, orgId) => {
  const headers = { 'Content-Type': 'application/json' };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await apiFetch(withOrg(`${API_BASE_URL}/supplier/custom`, orgId), {
    method: 'POST',
    headers,
    body: JSON.stringify({ name, loginUrl })
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const error = new Error(body?.Error || `supplier/custom failed (${response.status})`);
    error.userMessage = response.status === 400 ? body?.Error : null;
    throw error;
  }

  return response.json();
};

/** One of the user's receipt files, fetched with their token. */
export const fetchReceiptFile = async (fileId, accessToken, orgId) => {
  const headers = {};
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await apiFetch(withOrg(`${API_BASE_URL}/receipt/file?id=${encodeURIComponent(fileId)}`, orgId), { headers });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`receipt/file failed: ${response.status} ${text}`);
  }

  return response.blob();
};

/** Adds receipts by hand; returns { added: [name], skipped: [{ name, reason }] }. */
export const uploadReceipts = async (files, accessToken, orgId) => {
  const headers = {};
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const form = new FormData();
  Array.from(files).forEach((file) => form.append('files', file, file.name));

  const response = await apiFetch(withOrg(`${API_BASE_URL}/receipt/upload`, orgId), { method: 'POST', headers, body: form });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`receipt/upload failed: ${response.status} ${text}`);
  }

  return response.json();
};

/** The user's notifications in the selected org: { values: [{ id, message, read, createdAt }], unread }. */
export const fetchNotifications = async (accessToken, orgId) => {
  const headers = {};
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await apiFetch(withOrg(`${API_BASE_URL}/notifications`, orgId), { headers });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`notifications failed: ${response.status} ${text}`);
  }

  return response.json();
};

/** Marks notifications as read; with no ids, all of the user's in the selected org. */
export const markNotificationsRead = async (ids, accessToken, orgId) => {
  const headers = { 'Content-Type': 'application/json' };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await apiFetch(withOrg(`${API_BASE_URL}/notifications/read`, orgId), {
    method: 'POST',
    headers,
    body: JSON.stringify({ ids: ids || [] })
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`notifications/read failed: ${response.status} ${text}`);
  }

  return response.json();
};

/** One section of an org's settings: 'sso', 'integrations' or 'security'. */
export const fetchOrgSettings = async (section, accessToken, orgId) => {
  const headers = {};
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await apiFetch(withOrg(`${API_BASE_URL}/organisation/settings/${section}`, orgId), { headers });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`organisation/settings/${section} failed: ${response.status} ${text}`);
  }

  return response.json();
};

/** Saves one section of an org's settings; the API answers 400 with { Error } when a value is not valid. */
export const saveOrgSettings = async (section, value, accessToken, orgId) => {
  const headers = { 'Content-Type': 'application/json' };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await apiFetch(withOrg(`${API_BASE_URL}/organisation/settings/${section}`, orgId), {
    method: 'PUT',
    headers,
    body: JSON.stringify(value)
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const error = new Error(body?.Error || `Saving ${section} failed (${response.status})`);
    error.userMessage = response.status === 400 ? body?.Error : null;
    throw error;
  }

  return response.json();
};

/** Replaces the org's webhook signing key: { signingKeyRotatedAt }. */
export const rotateSigningKey = async (accessToken, orgId) => {
  const headers = {};
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await apiFetch(withOrg(`${API_BASE_URL}/organisation/settings/signing/rotate`, orgId), { method: 'POST', headers });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`signing/rotate failed: ${response.status} ${text}`);
  }

  return response.json();
};
